import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { CustomerLayout } from "@/components/CustomerLayout";
import { AttributionTracker } from "@/components/AttributionTracker";
import { ConsentBanner } from "@/components/ConsentBanner";
import { InstallPrompt } from "@/components/InstallPrompt";
import { LaunchGate } from "@/components/LaunchGate";

import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Home from "@/pages/customer/Home";
import Explore from "@/pages/customer/Explore";
import PropertyDetails from "@/pages/customer/PropertyDetails";
import Booking from "@/pages/customer/Booking";
import BookingConfirmed from "@/pages/customer/BookingConfirmed";
import MyBookings from "@/pages/customer/MyBookings";
import BookingDetails from "@/pages/customer/BookingDetails";
import Invoice from "@/pages/customer/Invoice";
import Payments from "@/pages/customer/Payments";
import SupportCenter from "@/pages/SupportCenter";
import CategoryPage from "@/pages/customer/CategoryPage";
import Favorites from "@/pages/customer/Favorites";
import Profile from "@/pages/customer/Profile";
import Refer from "@/pages/customer/Refer";
import Notifications from "@/pages/Notifications";
import About from "@/pages/public/About";
import Contact from "@/pages/public/Contact";
import Help from "@/pages/public/Help";
import ListYourProperty from "@/pages/public/ListYourProperty";
import LandingPage from "@/pages/public/LandingPage";
import Blog from "@/pages/public/Blog";
import BlogPost from "@/pages/public/BlogPost";
import { Terms, Privacy, CancellationPolicy, RefundPolicy } from "@/pages/public/Legal";

import OwnerDashboard from "@/pages/owner/OwnerDashboard";
import OwnerProperties from "@/pages/owner/OwnerProperties";
import PropertyForm from "@/pages/owner/PropertyForm";
import OwnerBookings from "@/pages/owner/OwnerBookings";
import OwnerCalendar from "@/pages/owner/OwnerCalendar";
import OwnerEarnings from "@/pages/owner/OwnerEarnings";
import OwnerStatement from "@/pages/owner/OwnerStatement";
import OwnerProfile from "@/pages/owner/OwnerProfile";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProperties from "@/pages/admin/AdminProperties";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBookings from "@/pages/admin/AdminBookings";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminFinance from "@/pages/admin/AdminFinance";
import AdminPayouts from "@/pages/admin/AdminPayouts";
import AdminRefunds from "@/pages/admin/AdminRefunds";
import AdminReconciliation from "@/pages/admin/AdminReconciliation";
import AdminDisputes from "@/pages/admin/AdminDisputes";
import AdminSupport from "@/pages/admin/AdminSupport";
import AdminOperations from "@/pages/admin/AdminOperations";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminOwnerLeads from "@/pages/admin/AdminOwnerLeads";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminWhatsApp from "@/pages/admin/AdminWhatsApp";
import AdminSeo from "@/pages/admin/AdminSeo";
import AdminLaunch from "@/pages/admin/AdminLaunch";
import AdminLaunchDashboard from "@/pages/admin/AdminLaunchDashboard";
import AdminOwnerKit from "@/pages/admin/AdminOwnerKit";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminMarketing from "@/pages/admin/AdminMarketing";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminReferrals from "@/pages/admin/AdminReferrals";
import AdminSettings from "@/pages/admin/AdminSettings";

const Customer = ({ children }) => <CustomerLayout>{children}</CustomerLayout>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AttributionTracker />
          <LaunchGate>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Public / customer mobile app */}
            <Route path="/" element={<Customer><Home /></Customer>} />
            <Route path="/explore" element={<Customer><Explore /></Customer>} />
            <Route path="/farmhouses" element={<Customer><CategoryPage category="Farmhouses" title="Farmhouses in Hyderabad" subtitle="Find the perfect farmhouse for your family gathering, celebration or stay." /></Customer>} />
            <Route path="/resorts" element={<Customer><CategoryPage category="Resorts" title="Resorts near you" subtitle="Relax and unwind at premium resorts across the region." /></Customer>} />
            <Route path="/banquet-halls" element={<Customer><CategoryPage category="Banquet Halls" title="Banquet Halls" subtitle="Grand banquet halls for weddings, receptions and big celebrations." /></Customer>} />
            <Route path="/function-halls" element={<Customer><CategoryPage category="Function Halls" title="Function Halls" subtitle="Elegant function halls for every event and occasion." /></Customer>} />
            <Route path="/about" element={<Customer><About /></Customer>} />
            <Route path="/contact" element={<Customer><Contact /></Customer>} />
            <Route path="/help" element={<Customer><Help /></Customer>} />
            <Route path="/list-your-property" element={<Customer><ListYourProperty /></Customer>} />
            {/* SEO / campaign landing pages */}
            <Route path="/hyderabad-farmhouses" element={<LandingPage category="Farmhouses" categorySlug="farmhouses" cityWide />} />
            <Route path="/hyderabad-resorts" element={<LandingPage category="Resorts" categorySlug="resorts" cityWide />} />
            <Route path="/hyderabad-banquet-halls" element={<LandingPage category="Banquet Halls" categorySlug="banquet-halls" cityWide />} />
            <Route path="/hyderabad-function-halls" element={<LandingPage category="Function Halls" categorySlug="function-halls" cityWide />} />
            <Route path="/farmhouses/:location" element={<LandingPage category="Farmhouses" categorySlug="farmhouses" />} />
            <Route path="/resorts/:location" element={<LandingPage category="Resorts" categorySlug="resorts" />} />
            <Route path="/banquet-halls/:location" element={<LandingPage category="Banquet Halls" categorySlug="banquet-halls" />} />
            <Route path="/function-halls/:location" element={<LandingPage category="Function Halls" categorySlug="function-halls" />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/terms" element={<Customer><Terms /></Customer>} />
            <Route path="/privacy" element={<Customer><Privacy /></Customer>} />
            <Route path="/cancellation-policy" element={<Customer><CancellationPolicy /></Customer>} />
            <Route path="/refund-policy" element={<Customer><RefundPolicy /></Customer>} />
            <Route path="/property/:id" element={<PropertyDetails />} />

            <Route path="/booking/:id" element={<ProtectedRoute roles={["customer"]}><Booking /></ProtectedRoute>} />
            <Route path="/booking-confirmed/:id" element={<ProtectedRoute roles={["customer"]}><BookingConfirmed /></ProtectedRoute>} />
            <Route path="/bookings" element={<ProtectedRoute roles={["customer"]}><Customer><MyBookings /></Customer></ProtectedRoute>} />
            <Route path="/bookings/:id" element={<ProtectedRoute roles={["customer"]}><Customer><BookingDetails /></Customer></ProtectedRoute>} />
            <Route path="/invoice/:id" element={<ProtectedRoute roles={["customer"]}><Invoice /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute roles={["customer"]}><Customer><Payments /></Customer></ProtectedRoute>} />
            <Route path="/support" element={<ProtectedRoute roles={["customer"]}><SupportCenter /></ProtectedRoute>} />
            <Route path="/favorites" element={<ProtectedRoute roles={["customer"]}><Customer><Favorites /></Customer></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute roles={["customer"]}><Customer><Profile /></Customer></ProtectedRoute>} />
            <Route path="/refer" element={<ProtectedRoute roles={["customer", "owner"]}><Customer><Refer /></Customer></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute roles={["customer", "owner", "admin"]}><Notifications /></ProtectedRoute>} />

            {/* Owner */}
            <Route path="/owner" element={<ProtectedRoute roles={["owner"]}><OwnerDashboard /></ProtectedRoute>} />
            <Route path="/owner/properties" element={<ProtectedRoute roles={["owner"]}><OwnerProperties /></ProtectedRoute>} />
            <Route path="/owner/properties/new" element={<ProtectedRoute roles={["owner"]}><PropertyForm /></ProtectedRoute>} />
            <Route path="/owner/properties/:id/edit" element={<ProtectedRoute roles={["owner"]}><PropertyForm /></ProtectedRoute>} />
            <Route path="/owner/bookings" element={<ProtectedRoute roles={["owner"]}><OwnerBookings /></ProtectedRoute>} />
            <Route path="/owner/calendar" element={<ProtectedRoute roles={["owner"]}><OwnerCalendar /></ProtectedRoute>} />
            <Route path="/owner/earnings" element={<ProtectedRoute roles={["owner"]}><OwnerEarnings /></ProtectedRoute>} />
            <Route path="/owner/statement" element={<ProtectedRoute roles={["owner"]}><OwnerStatement /></ProtectedRoute>} />
            <Route path="/owner/support" element={<ProtectedRoute roles={["owner"]}><SupportCenter /></ProtectedRoute>} />
            <Route path="/owner/profile" element={<ProtectedRoute roles={["owner"]}><OwnerProfile /></ProtectedRoute>} />

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/properties" element={<ProtectedRoute roles={["admin"]}><AdminProperties /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/bookings" element={<ProtectedRoute roles={["admin"]}><AdminBookings /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute roles={["admin"]}><AdminPayments /></ProtectedRoute>} />
            <Route path="/admin/finance" element={<ProtectedRoute roles={["admin"]}><AdminFinance /></ProtectedRoute>} />
            <Route path="/admin/payouts" element={<ProtectedRoute roles={["admin"]}><AdminPayouts /></ProtectedRoute>} />
            <Route path="/admin/refunds" element={<ProtectedRoute roles={["admin"]}><AdminRefunds /></ProtectedRoute>} />
            <Route path="/admin/reconciliation" element={<ProtectedRoute roles={["admin"]}><AdminReconciliation /></ProtectedRoute>} />
            <Route path="/admin/operations" element={<ProtectedRoute roles={["admin"]}><AdminOperations /></ProtectedRoute>} />
            <Route path="/admin/disputes" element={<ProtectedRoute roles={["admin"]}><AdminDisputes /></ProtectedRoute>} />
            <Route path="/admin/support" element={<ProtectedRoute roles={["admin"]}><AdminSupport /></ProtectedRoute>} />
            <Route path="/admin/staff" element={<ProtectedRoute roles={["admin"]}><AdminStaff /></ProtectedRoute>} />
            <Route path="/admin/leads" element={<ProtectedRoute roles={["admin"]}><AdminLeads /></ProtectedRoute>} />
            <Route path="/admin/owner-leads" element={<ProtectedRoute roles={["admin"]}><AdminOwnerLeads /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute roles={["admin"]}><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/whatsapp" element={<ProtectedRoute roles={["admin"]}><AdminWhatsApp /></ProtectedRoute>} />
            <Route path="/admin/seo" element={<ProtectedRoute roles={["admin"]}><AdminSeo /></ProtectedRoute>} />
            <Route path="/admin/launch" element={<ProtectedRoute roles={["admin"]}><AdminLaunch /></ProtectedRoute>} />
            <Route path="/admin/launch-dashboard" element={<ProtectedRoute roles={["admin"]}><AdminLaunchDashboard /></ProtectedRoute>} />
            <Route path="/admin/marketing-kit" element={<ProtectedRoute roles={["admin"]}><AdminOwnerKit /></ProtectedRoute>} />
            <Route path="/admin/marketplace" element={<ProtectedRoute roles={["admin"]}><AdminMarketplace /></ProtectedRoute>} />
            <Route path="/admin/coupons" element={<ProtectedRoute roles={["admin"]}><AdminCoupons /></ProtectedRoute>} />
            <Route path="/admin/marketing" element={<ProtectedRoute roles={["admin"]}><AdminMarketing /></ProtectedRoute>} />
            <Route path="/admin/campaigns" element={<ProtectedRoute roles={["admin"]}><AdminCampaigns /></ProtectedRoute>} />
            <Route path="/admin/referrals" element={<ProtectedRoute roles={["admin"]}><AdminReferrals /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>} />
          </Routes>
          </LaunchGate>
          <ConsentBanner />
          <InstallPrompt />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
